import { fbt, useLocaleContext } from 'fbtee'
import { useAuth } from '../../hooks/useAuth'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { useMyRequests } from '../../hooks/useMyRequests'
import { useRequestsUnread } from '../../hooks/useRequestsUnread'
import { usePageLoadTrace } from '../../lib/perf'
import { myRequestsPrompt } from '../../lib/guestPrompts'
import { placeholders } from '../../lib/skeletonUtils'
import { PageLayout } from '../../components/PageLayout'
import { EmptyState } from '../../components/EmptyState'
import { ErrorState } from '../../components/ErrorState'
import { Button } from '../../components/ui/button'
import { RequestRow } from './RequestRow'

export default function MyRequestsPage() {
  useDocumentTitle(pageTitle(fbt('My requests', 'Document title for the My requests page')))
  const { locale } = useLocaleContext()
  const { signedInUid, loading: authLoading } = useAuth()
  const { isGuest, prompt } = useGuestGuard()
  const { items, loading: requestsLoading, error } = useMyRequests(signedInUid)
  // Having the panel open is what marks everything as read.
  useRequestsUnread(true)

  const loading = authLoading || requestsLoading
  usePageLoadTrace('requests_page_to_content', !loading)

  return (
    <PageLayout
      back="/settings"
      title={
        <p className="font-semibold text-sm leading-none">
          <fbt desc="Heading for the My requests page">My requests</fbt>
        </p>
      }
    >
      {!authLoading && isGuest ? (
        <EmptyState
          action={
            <Button size="sm" onClick={() => prompt(myRequestsPrompt())}>
              <fbt desc="Button on the My requests page that opens the log-in dialog for guests">Log in</fbt>
            </Button>
          }
        >
          <fbt desc="Empty state on the My requests page for a guest">
            Log in to see what happened to the events, set times and areas you've asked for.
          </fbt>
        </EmptyState>
      ) : loading ? (
        <ul className="space-y-2">
          {placeholders(3).map((_, i) => (
            <RequestRow key={i} loading />
          ))}
        </ul>
      ) : error && items.length === 0 ? (
        <ErrorState error={error} />
      ) : items.length === 0 ? (
        <EmptyState>
          <fbt desc="Empty state on the My requests page when the user has submitted nothing">
            Nothing submitted yet. Suggest an event, report set times, or send feedback and it'll show up here.
          </fbt>
        </EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="my-requests-list">
          {items.map(item => (
            <RequestRow key={`${item.kind}-${item.id}`} item={item} locale={locale} />
          ))}
        </ul>
      )}
    </PageLayout>
  )
}
